import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  Drawer, Field, QueryState, RecordTable, Select, StatusPill, formatDate, todayIso, type Column,
} from '@/components/live';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { allStates, areaUnits } from '@/api/lookups';
import { useAuth } from '@/auth/AuthProvider';
import { isAdmin } from '@/auth/session';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';
import { AreaUnitAdd } from '@/screens/parts/AreaUnitAdd';

interface ConversionRow {
  id: string;
  from_unit_id: string;
  unit_code: string;
  unit_label: string;
  factor: number;
  geography_id: string | null;
  geography_name: string | null;
  source_note: string | null;
  status: string;
  effective_from: string | null;
  effective_to: string | null;
}

/**
 * Land units and what one of each is in hectares, by place.
 *
 * Hectares are what EUDR and FSC reporting read; land records are kept in Bigha, Katha, Acre,
 * Guntha and so on, and some of those units are a different size in each state. So a factor is
 * set per unit, and for a unit that varies, per state. Seeded regional factors arrive as
 * UNCONFIRMED (`draft`): they are used, and the plot form says so, until someone who holds the
 * state's land records confirms or corrects them here.
 *
 * Correcting a factor never overwrites it: the old one is closed with an end date, so a hectare
 * figure computed with it can still be explained, and every plot using the unit is re-derived.
 */
export function AreaConversionsPanel() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const admin = isAdmin(profile);
  // The factors are the application's, so only the app owner changes them; an organisation admin
  // can still add a unit name of its own.
  const appOwner = profile?.tier === 0;
  const [stat, setStat] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ unit_id: string; geography_id: string; factor: string; note: string; confirmed: boolean } | null>(null);
  const [addingUnit, setAddingUnit] = useState(false);
  const [saving, setSaving] = useState(false);

  const rows = useRows<ConversionRow>('area_conversions', { orderBy: { column: 'unit_label' } });
  const units = useQuery({ queryKey: ['ref', 'unit', 'area'], queryFn: areaUnits, enabled: isSupabaseConfigured });
  const states = useQuery({
    queryKey: ['picker', 'geography', 'all-states'], queryFn: allStates,
    enabled: isSupabaseConfigured && Boolean(editing),
  });

  const today = todayIso();
  const current = useMemo(
    () => (rows.data ?? []).filter((row) => !row.effective_to || row.effective_to >= today),
    [rows.data, today],
  );

  const STATS = useMemo(() => [
    { key: 'all', label: 'Factors', match: () => true, tone: 'neutral' as const },
    { key: 'draft', label: 'Unconfirmed', tone: 'warn' as const, match: (row: ConversionRow) => row.status === 'draft' },
    { key: 'active', label: 'Confirmed', tone: 'ok' as const, match: (row: ConversionRow) => row.status === 'active' },
    { key: 'regional', label: 'State-specific', tone: 'info' as const, match: (row: ConversionRow) => Boolean(row.geography_id) },
  ], []);
  const visible = useMemo(() => applyStat(current, STATS, stat), [current, STATS, stat]);

  const chosenUnit = (units.data ?? []).find((unit) => unit.id === editing?.unit_id);

  const columns: Array<Column<ConversionRow>> = [
    { key: 'unit_label', label: 'Unit', render: (row) => <strong>{row.unit_label}</strong> },
    { key: 'geography_name', label: 'Applies in', render: (row) => row.geography_name ?? 'Everywhere' },
    { key: 'factor', label: '1 unit = ha', align: 'right', render: (row) => Number(row.factor).toFixed(3) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status === 'draft' ? 'pending' : row.status === 'active' ? 'verified' : row.status} /> },
    { key: 'source_note', label: 'Source', render: (row) => row.source_note ?? '--' },
    { key: 'effective_from', label: 'Since', render: (row) => formatDate(row.effective_from) },
    {
      key: 'act', label: '',
      render: (row) => (appOwner && row.from_unit_id ? (
        <button onClick={() => setEditing({
          unit_id: row.from_unit_id, geography_id: row.geography_id ?? '', factor: String(row.factor),
          note: row.source_note ?? '', confirmed: true,
        })} type="button">{row.status === 'draft' ? 'Confirm / correct' : 'Correct'}</button>
      ) : null),
    },
  ];

  async function save() {
    if (!editing) return;
    const factor = Number(editing.factor);
    if (!editing.unit_id) { toast.error('Choose the unit.'); return; }
    if (!(factor > 0)) { toast.error('Enter how many hectares one unit is.'); return; }
    setSaving(true);
    try {
      await callRpc('fn_save_area_conversion', {
        p_unit_id: editing.unit_id, p_factor: factor, p_geography_id: editing.geography_id || null,
        p_note: editing.note.trim() || null, p_confirmed: editing.confirmed,
      });
      await queryClient.invalidateQueries({ queryKey: ['area_conversions'] });
      await queryClient.invalidateQueries({ queryKey: ['farm_plots'] });
      toast.success('Factor saved. Plots using this unit have been recalculated.');
      setEditing(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The factor could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="settings-section">
      <div className="toolbar-row">
        <div>
          <h2 className="section-title">Land units and hectare factors</h2>
          <p className="muted" style={{ margin: 0 }}>
            Plots are reported in hectares. Units such as Bigha and Katha differ by state, so each gets a factor per state.
            The factors are the same for every organisation{appOwner ? '' : ', and are set by the app owner'}.
          </p>
        </div>
        {admin && (
          <div className="toolbar-actions">
            <button onClick={() => setAddingUnit(true)} type="button"><Plus size={16} /> New unit</button>
            {appOwner && (
              <button className="primary" onClick={() => setEditing({ unit_id: '', geography_id: '', factor: '', note: '', confirmed: true })}
                      type="button"><Plus size={16} /> Add factor</button>
            )}
          </div>
        )}
      </div>

      {addingUnit && <AreaUnitAdd onAdded={() => setAddingUnit(false)} onCancel={() => setAddingUnit(false)} />}

      <StatCards active={stat} onSelect={setStat} rows={current} stats={STATS} />
      <QueryState empty="No conversion factors yet." query={rows}>
        <RecordTable columns={columns} rows={visible} />
      </QueryState>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setEditing(null)} type="button">Cancel</button>
            <button className="primary" disabled={saving} onClick={() => void save()} type="button">
              {saving ? 'Saving...' : 'Save factor'}
            </button>
          </>
        }
        onClose={() => setEditing(null)}
        open={Boolean(editing)}
        subtitle="For every organisation. The previous factor is kept with an end date, and plots using this unit are recalculated everywhere."
        title="Hectare factor"
      >
        {editing && (
          <>
            <Field label="Unit" required>
              <Select onChange={(value) => setEditing({ ...editing, unit_id: value })}
                      options={(units.data ?? []).map((unit) => ({ id: unit.id, label: unit.label }))}
                      value={editing.unit_id} />
            </Field>
            <Field hint={chosenUnit && !chosenUnit.is_fixed ? 'This unit varies by state, so a state is required.' : 'Leave empty for a factor that holds everywhere.'}
                   label="Applies in state" required={Boolean(chosenUnit && !chosenUnit.is_fixed)}>
              <Select onChange={(value) => setEditing({ ...editing, geography_id: value })}
                      options={states.data ?? []} placeholder="Everywhere" value={editing.geography_id} />
            </Field>
            <Field label="1 unit = ha" required>
              <input inputMode="decimal" onChange={(event) => setEditing({ ...editing, factor: event.target.value })}
                     value={editing.factor} />
            </Field>
            <Field label="Source">
              <input onChange={(event) => setEditing({ ...editing, note: event.target.value })}
                     placeholder="e.g. State land records notification" value={editing.note} />
            </Field>
            <label className="check-row">
              <input checked={editing.confirmed} onChange={(event) => setEditing({ ...editing, confirmed: event.target.checked })} type="checkbox" />
              Confirmed against the state's land records
            </label>
          </>
        )}
      </Drawer>
    </section>
  );
}
